/**
 * Top-level paths that live OUTSIDE the `[locale]` app segment and must never
 * be locale-prefixed (admin, api, studio, auth callbacks, sitemap, robots, …).
 *
 * Single source of truth for:
 *  - src/proxy.ts — skips the next-intl rewrite for these paths;
 *  - src/lib/content/pt-link.ts — rich-text links to these paths render as a
 *    plain <a> (href unchanged) instead of through the locale-aware <Link>,
 *    which would turn `/api/x` into `/ko/api/x` (a 404);
 *  - scripts/validate-non-localized-routes.js — asserts each prefix has a
 *    handler under src/app (it reads the array literal below from THIS file).
 *
 * Plain .ts, no Next/React imports — safe for the Edge proxy, client
 * components and node:test alike.
 *
 * Entry notes (kept OUTSIDE the array literal — see NB below):
 *   /app — legacy mobile share links (/app/<type>/<id>), redirected to
 *   canonical content URLs by the route handler under src/app/app/.
 *   English-only by construction: the app never emitted locale-prefixed
 *   share URLs.
 *
 * NB: the array literal below must stay free of comments — and of any
 * quoted text or closing square bracket between its opening and closing
 * brackets. scripts/validate-non-localized-routes.js captures the block
 * with a regex that stops at the FIRST closing bracket and treats EVERY
 * quoted run inside as a prefix entry, so a comment inside the literal
 * can silently truncate validation or inject phantom prefixes.
 */
export const NON_LOCALIZED_PATH_PREFIXES = [
  '/admin',
  '/studio',
  '/api',
  '/app',
  '/callback',
  '/sitemap',
  '/robots',
  '/llms',
  '/superpwa-sw',
  '/.well-known',
  '/r',
  '/_next',
] as const

/**
 * True when `pathname` (no query/hash) is, or sits under, a non-localized
 * prefix. Also matches file-extension siblings of a prefix entry — e.g.
 * `/sitemap` covers `/sitemap.xml`, `/robots` covers `/robots.txt`, `/llms`
 * covers `/llms.txt`. Without that, the intl middleware rewrites
 * `/sitemap.xml` → `/en/sitemap.xml`, which doesn't exist as a Next.js
 * convention file → 404.
 */
export function isNonLocalizedPath(pathname: string): boolean {
  return NON_LOCALIZED_PATH_PREFIXES.some(
    (p) =>
      pathname === p ||
      pathname.startsWith(p + '/') ||
      pathname.startsWith(p + '.'),
  )
}
